import { useEffect, useRef } from 'react';

interface ModalA11yOptions {
  /** Whether the modal is currently shown. */
  active?: boolean;
  /** Called when Escape is pressed (or backdrop click — but that's owned by the modal itself). */
  onEscape: () => void;
}

/**
 * Shared accessibility primitives for portaled modals.
 *
 * Behavior contract (matches enterprise-modal spec):
 *   - Body scroll is locked while the modal is mounted.
 *   - Escape closes (delegates to onEscape).
 *   - Focus is moved into the dialog on mount, restored on unmount.
 *   - Focus is trapped inside the dialog (Tab / Shift+Tab cycle).
 *   - role="dialog" + aria-modal="true" should be set on the returned ref'd element.
 *
 * Usage:
 *   const dialogRef = useModalA11y({ onEscape: onClose });
 *   <div ref={dialogRef} role="dialog" aria-modal="true">...</div>
 */
export function useModalA11y({ active = true, onEscape }: ModalA11yOptions) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  useEffect(() => {
    if (!active) return;

    previousActiveElement.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into dialog
    queueMicrotask(() => {
      const el = dialogRef.current;
      if (!el) return;
      // Try to focus first interactive descendant; fall back to dialog itself.
      const focusable = el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (focusable || el).focus();
    });

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;

      // Focus trap: keep tab cycle inside the dialog
      const el = dialogRef.current;
      if (!el) return;
      const items = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter(n => !n.hasAttribute('disabled') && n.tabIndex !== -1);
      if (items.length === 0) {
        e.preventDefault();
        el.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (current === first || !el.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (current === last || !el.contains(current)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
      const prev = previousActiveElement.current as HTMLElement | null;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [active, onEscape]);

  return dialogRef;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'video[controls]',
  'audio[controls]',
  'iframe',
  '[contenteditable="true"]',
].join(',');
